import { useQueryClient } from "@tanstack/react-query";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  ISectionProps,
  PatientNotesTableContent,
} from "../../../../@types/Patient";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { Dots } from "../../../../assets/icons/Dots";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { Dropdown, DropdownTextItem } from "../../../../components/Dropdown";
import { ActionContainer } from "../../../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import { Tag } from "../../../../components/Tag";
import { noteTypes } from "../../../../constants/noteTypes";
import {
  invalidateAllNoteCaches,
  useToggleNoteComplete,
  useToggleNoteUrgent,
} from "../../../../hooks/mutations/notes";
import { useInfiniteNotes } from "../../../../hooks/queries/notes";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { PatientNotesTable } from "../PatientNotesTable";
import { DeleteNoteModal } from "../PatientNotesTable/components/DeleteNoteModal";
import { EditNoteModal } from "../PatientNotesTable/components/EditNoteModal";
import { ViewNoteModal } from "../PatientNotesTable/components/ViewNoteModal";
import {
  NewDataCOntainer,
  TagContainer,
  TitleContainer,
} from "./NotesSection.styles";

// 3-dots menu labels are also used as dropdown values, so they live as constants
// to keep the click-handler switch in sync with the rendered option list.
// EN-5191: ids, not labels. The switch below matches on these, so the menu's display text
// is resolved separately at render time.
const MENU_EDIT = "edit";
const MENU_DELETE = "delete";
const MENU_MARK_URGENT = "markUrgent";
const MENU_MARK_NOT_URGENT = "markNotUrgent";
const MENU_MARK_COMPLETE = "markComplete";
const MENU_MARK_NOT_COMPLETE = "markNotComplete";

// Zebra-striped pink for active urgent task rows. Same pair as the patient
// page alerts table so the visual language is consistent.
const URGENT_ROW_HIGHLIGHT_COLORS = [
  "rgba(250, 209, 217, 0.48)",
  "rgba(250, 209, 217, 0.28)",
];

// Plain notes show "Note only"; tasks show either the red Active chip while
// pending or the completion timestamp once closed.
function renderCompleteCell(note: PatientNotesTableContent, t: TFunction) {
  if (note.noteType !== noteTypes.TASK) {
    return t("patientPage_notes_noteOnly");
  }
  if (note.isCompleted) {
    return note.completedAt ? getLongFormattedDate(note.completedAt) : "-";
  }
  return (
    <TagContainer>
      <Tag variant="error">{t("patientPage_notes_active")}</Tag>
    </TagContainer>
  );
}

// Priority only makes sense on active tasks. Completed tasks keep is_urgent
// server-side (for reactivation) but hide it visually so users see a clean state.
function renderPriorityCell(note: PatientNotesTableContent, t: TFunction) {
  if (note.noteType === noteTypes.TASK && note.isUrgent && !note.isCompleted) {
    return (
      <TagContainer>
        <Tag variant="error">{t("patientPage_notes_urgent")}</Tag>
      </TagContainer>
    );
  }
  return "-";
}

export function NotesSection({
  patient,
  updateColapsedSections,
  collapsedSection,
}: ISectionProps) {
  const { t } = useTranslation();

  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const { openModal, closeModal } = useModal();
  const { isMobile } = useIsMobile();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasOpenedDeepLinkedNote = useRef(false);
  const queryClient = useQueryClient();

  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [showNewData, setShowNewData] = useState<boolean>(false);

  const {
    notes: patientNoteTable,
    isError,
    isFetching,
    refetch,
    isRefetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteNotes(patient.id);

  const { mutateAsync: toggleUrgent } = useToggleNoteUrgent();
  const { mutateAsync: toggleComplete } = useToggleNoteComplete();

  const handleRefreshTable = () => {
    refetch();
  };

  const handleEditNoteClose = () => {
    // Edits to a task here must also reach My Tasks (it's the same record on
    // both pages). Local refetch covers the patient page; invalidateAll keeps
    // the My Tasks list and the single-note cache in sync.
    invalidateAllNoteCaches(queryClient);
    refetch();
    closeModal();
  };

  const handleDeleteNoteClose = () => {
    invalidateAllNoteCaches(queryClient);
    refetch();
    closeModal();
  };

  const handleChildNotification = (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => {
    if (notificationMessage && notificationType) {
      showToast(notificationMessage, notificationType);
    }
  };

  const handleEditNote = (noteId: number) => {
    openModal(
      <EditNoteModal
        noteId={noteId}
        setNotification={handleChildNotification}
        onClose={handleEditNoteClose}
      />,
      {
        width: "auto",
        height: "100%",
        showCloseButton: true,
      }
    );
  };

  // Deep link from "tagged in a note" / "assigned a task" email: ?noteId=123
  // opens the record in view mode. Strip the param after opening so refreshing
  // the page doesn't reopen it.
  useEffect(() => {
    if (hasOpenedDeepLinkedNote.current) return;
    const rawNoteId = searchParams.get("noteId");
    if (!rawNoteId) return;

    const parsedNoteId = Number(rawNoteId);
    if (!Number.isInteger(parsedNoteId) || parsedNoteId <= 0) return;

    hasOpenedDeepLinkedNote.current = true;

    openModal(
      <ViewNoteModal noteId={parsedNoteId} onEditClick={handleEditNote} />,
      {
        width: "auto",
        height: "auto",
        showCloseButton: true,
      }
    );

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("noteId");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams]);

  const highlightedRows = useMemo(() => {
    if (!patientNoteTable) return [false];
    return patientNoteTable.map(
      (note) =>
        note.noteType === noteTypes.TASK && note.isUrgent && !note.isCompleted
    );
  }, [patientNoteTable]);

  const handleToggleUrgent = async (
    noteId: number,
    currentlyUrgent: boolean
  ) => {
    try {
      await toggleUrgent({ noteId, isUrgent: !currentlyUrgent });
      showToast(
        currentlyUrgent
          ? t("patientPage_notes_toasts_unmarkedUrgent")
          : t("patientPage_notes_toasts_markedUrgent"),
        "success"
      );
    } catch {
      showToast(t("patientPage_notes_toasts_urgentFailed"), "error");
    }
  };

  const handleToggleComplete = async (
    noteId: number,
    currentlyComplete: boolean
  ) => {
    try {
      await toggleComplete({ noteId, isCompleted: !currentlyComplete });
      showToast(
        currentlyComplete
          ? t("patientPage_notes_toasts_markedNotComplete")
          : t("patientPage_notes_toasts_markedComplete"),
        "success"
      );
    } catch {
      showToast(t("patientPage_notes_toasts_completionFailed"), "error");
    }
  };

  const parsePatientNotesToTable = useMemo(() => {
    if (!patientNoteTable) return [];

    const handleOnChangeDropdownVisibility = (
      noteId: number,
      isOpen: boolean
    ) => {
      setIsDropDownOpen(isOpen);
      setSelectedRowId(isOpen ? noteId : null);
    };

    const handleDeleteNote = (noteId: number) => {
      openModal(
        <DeleteNoteModal
          noteId={noteId}
          setNotification={handleChildNotification}
          onClose={handleDeleteNoteClose}
        />,
        {
          width: "auto",
          height: "100%",
          showCloseButton: true,
        }
      );
    };

    return patientNoteTable.map((patientNote: PatientNotesTableContent) => {
      const options: string[] = [];
      if (patientNote.noteType === noteTypes.TASK) {
        options.push(
          patientNote.isUrgent ? MENU_MARK_NOT_URGENT : MENU_MARK_URGENT
        );
        options.push(
          patientNote.isCompleted ? MENU_MARK_NOT_COMPLETE : MENU_MARK_COMPLETE
        );
      }
      if (patientNote.editable) {
        options.push(MENU_EDIT, MENU_DELETE);
      }

      const completeCell = renderCompleteCell(patientNote, t);
      const priorityCell = renderPriorityCell(patientNote, t);
      const assignedToCell = patientNote.assignedHcpName ?? "-";

      return {
        id: patientNote.id,
        notes: patientNote.notes,
        assignedTo: assignedToCell,
        complete: completeCell,
        time: getLongFormattedDate(patientNote.time),
        priority: priorityCell,
        reviewedByEmail: patientNote.reviewedByEmail,
        noteType: patientNote.noteType,
        isUrgent: patientNote.isUrgent,
        isCompleted: patientNote.isCompleted,
        action: (
          <div onClick={(e) => e.stopPropagation()} role="presentation">
            {options.length === 0 ? (
              <Tooltip
                title={t("patientPage_notes_ownNotesOnly")}
                zIndex={3002}
                color="#4B4B4B"
                arrow={false}
                styles={{
                  body: {
                    minHeight: "40px",
                    minWidth: "220px",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                }}
              >
                <ActionContainer className="actionContainer">
                  <CustomIconButton>
                    <Dots />
                  </CustomIconButton>
                </ActionContainer>
              </Tooltip>
            ) : (
              <ActionContainer className="actionContainer">
                <Dropdown
                  trigger={
                    <CustomIconButton>
                      <Dots />
                    </CustomIconButton>
                  }
                  value=""
                  onOpenChange={(val: boolean) =>
                    handleOnChangeDropdownVisibility(patientNote.id, val)
                  }
                  onValueChange={(val: string) => {
                    switch (val) {
                      case MENU_DELETE:
                        handleDeleteNote(patientNote.id);
                        break;
                      case MENU_EDIT:
                        handleEditNote(patientNote.id);
                        break;
                      case MENU_MARK_URGENT:
                      case MENU_MARK_NOT_URGENT:
                        handleToggleUrgent(
                          patientNote.id,
                          patientNote.isUrgent
                        );
                        break;
                      case MENU_MARK_COMPLETE:
                      case MENU_MARK_NOT_COMPLETE:
                        handleToggleComplete(
                          patientNote.id,
                          patientNote.isCompleted
                        );
                        break;
                      default:
                        break;
                    }
                  }}
                >
                  {options.map((option) => (
                    <DropdownTextItem
                      key={option}
                      value={option}
                      text={t(`patientPage_notes_menu_${option}`)}
                    />
                  ))}
                </Dropdown>
              </ActionContainer>
            )}
          </div>
        ),
      } as unknown as PatientNotesTableContent;
    });
  }, [patientNoteTable, selectedRowId, t]);

  useEffect(() => {
    if (
      collapsedSection.value === true &&
      !isFetching &&
      !isRefetching &&
      patientNoteTable.length > 0
    ) {
      const mostRecentNoteDate = dayjs(patientNoteTable[0].time);
      const collapsedDate = dayjs(collapsedSection.collapsedDate);

      if (mostRecentNoteDate > collapsedDate) {
        setShowNewData(true);
      }
    }
  }, [patientNoteTable]);

  const handleCollapseSection = () => {
    if (collapsedSection.value === true) {
      setShowNewData(false);
    }
    updateColapsedSections("notesSection");
  };

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PageTitle>{t("patientPage_sections_notes")}</PageTitle>
          {showNewData && (
            <NewDataCOntainer>
              {t("patientPage_sections_newData")}
            </NewDataCOntainer>
          )}
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}
      <div
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <PatientNotesTable
          patientId={patient.id}
          isLoading={isFetching && !isRefetching && !isFetchingNextPage}
          isError={isError}
          content={parsePatientNotesToTable}
          highlightedRows={highlightedRows}
          highlightColor={URGENT_ROW_HIGHLIGHT_COLORS}
          refreshTable={handleRefreshTable}
          handleEditNote={handleEditNote}
          isDropDownOpen={isDropDownOpen}
          showAddNoteButton={!collapsedSection.value}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          useInfiniteScroll
        />
      </div>
    </>
  );
}
